import { CircularProgress } from "@mui/material";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover } from "components/Popover/Popover";
import RecordTable from "components/Table/RecordTable";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import Tooltip from "components/Tooltip/Tooltip";
import { Wrapper } from "graphql/generated/graphql";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./ResourceWrapper.module.scss";
import { actions as wrapperListActions } from "./WrapperList.reducer";
import { getWrappers } from "./WrapperList.selectors";

interface AddWrapperComponentProps {
    createResourceWrapper: (wrapperId: string) => void;
    canAddWrapper: boolean;
    isDisabledWithTooltip: boolean;
    isListLoading?: boolean;
}

const initialQuery = { offset: 0, order: "+name", limit: 50 as 50 };

export const AddWrapperComponent = ({
    createResourceWrapper,
    canAddWrapper,
    isDisabledWithTooltip,
    isListLoading,
}: AddWrapperComponentProps) => {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState(false);

    const wrapperList: Wrapper[] = useSelector(getWrappers);

    const columns = [
        {
            label: "Name",
            key: "name",
            shown: true,
            sortable: false,
            headerRenderer: () => null,
            dto: ({ name, id }) => {
                return (
                    <span
                        key={id}
                        onClick={() => createResourceWrapper(id)}
                        className="wrapperLabel"
                    >
                        {name}
                    </span>
                );
            },
        },
    ];

    const fetchWrappers = useCallback(
        () =>
            new Promise((resolve, reject) => {
                dispatch({
                    promise: { resolve, reject },
                    // Fetch the available wrappers through the dedicated
                    // graphqlPost-based saga (WrapperList.saga), matching the
                    // sibling ResourceWrapperContainer. The generic Listing saga
                    // issues a GET to /graphql, which the backend rejects (GraphQL
                    // is POST-only), leaving the picker empty.
                    type: wrapperListActions.FETCH_LISTING_WRAPPER_LIST,
                    params: {
                        query: `query ($offset: Int, $order: String, $limit: Int){
                            wrapper_plain_list(offset: $offset, order: $order, limit: $limit)  {
                                id
                                name
                                input_schema
                            }
                        }`,
                        variables: {
                            offset: initialQuery.offset,
                            order: initialQuery.order,
                            limit: initialQuery.limit,
                        },
                    },
                });
            })
                .catch((error) => error)
                .finally(() => setLoading(false)),
        [dispatch]
    );

    const onClick = useCallback(() => {
        setLoading(true);
        fetchWrappers();
    }, [fetchWrappers]);

    const popover = (
        <Popover
            rootClassName="wrapperList__popover wrapper"
            contentClassName={styles.wrapperList__content}
            toggleClassName={
                canAddWrapper && !isDisabledWithTooltip ? "" : styles.disabledAddWrapperButton
            }
            anchorOrigin={{ vertical: 45, horizontal: "left" }}
            trigger={
                <Button
                    icon={IconCollection.codeBraces}
                    label="Add wrapper"
                    variation="dark"
                    onClick={onClick}
                    disabled={!canAddWrapper || isDisabledWithTooltip}
                />
            }
        >
            <div className="popover-content">
                <RecordTable
                    data={wrapperList}
                    options={{
                        density: "medium",
                        selectableRows: "none",
                        toolbar: "none",
                        toolbarFilters: false,
                        isSearchAlwaysOpen: true,
                        viewColumns: false,
                        customFooter: () => <></>,
                    }}
                    columns={columns}
                    isLoading={isLoading}
                    totalItemCount={wrapperList.length}
                    recordTypes={null}
                    initialQuery={initialQuery}
                />
            </div>
        </Popover>
    );

    return (
        <div className="addWrapperContainer">
            {isDisabledWithTooltip ? (
                <Tooltip overlay="To add new wrapper, save newly added first">{popover}</Tooltip>
            ) : (
                popover
            )}
            <DocsInfoTooltip title={"Add wrapper"} urlPath={"/docs/wrappers"} />
            {isListLoading && <CircularProgress disableShrink className="spinner" size={20} />}
        </div>
    );
};
